import { HttpStatusCode } from 'axios';
import { useSetAtom } from 'jotai';
import React, { PropsWithChildren, useEffect, useState } from 'react';

import { UserDetailsWithPermissionDto } from '@/api/generated';
import { FullPageSpinner } from '@/components/ui/fullPageSpinner/FullPageSpinner.comp.tsx';
import { userAtom } from '@/contexts/userContext/User.context.ts';
import { useGetCurrentUser } from '@/contexts/userContext/util/useGetCurrentUser.util.ts';
import { useSynchronizeMailbox } from '@/contexts/userContext/util/useSynchronizeMailbox.util.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { useAppNavigation } from '@/utils/useAppNavigation.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { Forbidden } from '@/views/generalError/forbidden/Forbidden.comp.tsx';
import { GeneralError } from '@/views/generalError/GeneralError.view.tsx';
import NotAuthorized from '@/views/generalError/notAuthorized/NotAuthorized.view.tsx';

const PATHS_WITHOUT_SESSION = [RouteEnum.NOT_AUTHORIZED, RouteEnum.WAITING_FOR_APPROVAL];

export const UserContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslationWithPrefix('utils.userContext');
  const [isInitialized, setIsInitialized] = useState(false);
  const setUser = useSetAtom(userAtom);
  const { location } = useAppNavigation();
  const pathWithoutSession = PATHS_WITHOUT_SESSION.map(path => path.toString()).includes(
    location.pathname,
  );

  const setUserOnSuccess = (newUser: UserDetailsWithPermissionDto) => {
    const fullName = `${newUser.lastName} ${newUser.firstName}`;
    setUser({
      ...newUser,
      dataReady: true,
      userSelectOption: {
        label: fullName,
        value: newUser.userId,
      },
    });
  };
  const { errorGettingUser, getCurrentUser, isGettingCurrentUser, isWaitingForBackend } =
    useGetCurrentUser();
  const { synchronizeMailbox } = useSynchronizeMailbox();

  const init = async () => {
    if (pathWithoutSession) {
      setIsInitialized(true);
      setUser(cur => ({
        ...cur,
        dataReady: true,
      }));
      return;
    }

    const { data } = await getCurrentUser();
    if (!data) {
      throw new Error('User not found');
    }

    setUserOnSuccess(data);
    setIsInitialized(true);
    void synchronizeMailbox(data.userId);
  };

  useEffect(() => {
    if (window.location.pathname === '/logout') {
      return;
    }

    void init();
  }, []);

  if (errorGettingUser?.response?.status === HttpStatusCode.Forbidden) {
    return <Forbidden />;
  }

  if (errorGettingUser?.response?.status === HttpStatusCode.Unauthorized) {
    return <NotAuthorized />;
  }

  if (errorGettingUser) {
    return <GeneralError hideButton />;
  }

  if (isWaitingForBackend) {
    return <FullPageSpinner caption={t('waitingForBackend')} />;
  }

  if (isGettingCurrentUser) {
    return <FullPageSpinner />;
  }

  if (!isInitialized) {
    return <FullPageSpinner />;
  }

  return <React.Fragment>{children}</React.Fragment>;
};
